import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { memo, type ReactNode, type RefObject } from 'react';

import { type EmojiMenuProps } from '@air/component-emoji-menu';
import { AtSignIcon, SmileIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { useBreakpointsContext } from '@air/provider-media-query';

import {
  InsertLinkButton,
  type InsertLinkButtonHandle,
} from '~/components/DiscussionsV2/DiscussionForm/InsertLinkButton';
import {
  DISCUSSION_FORM_BUTTON_CANCEL,
  DISCUSSION_FORM_BUTTON_SUBMIT,
  DISCUSSION_FORM_BUTTONS_CONTAINER,
} from '~/constants/testIDs';

const EmojiMenu = dynamic<EmojiMenuProps>(() => import('@air/component-emoji-menu').then((mod) => mod.EmojiMenu), {
  ssr: false,
});

export type CommentActionsProps = {
  onCancel: () => void;
  onEmojiPick: (emoji: string) => void;
  onLinkInsert: (text: string, url: string) => void;
  hasCancelButton?: boolean;
  isDisabled: boolean;
  isLoadingMentions?: boolean;
  addTriggerProgrammatically: () => void;
  shouldShowMentionsButton?: boolean;
  hasInternalAvatar?: boolean;
  onLinkCancel?: () => void;
  getSelectedText?: () => string;
  insertLinkButtonRef?: RefObject<InsertLinkButtonHandle>;
  helperActions?: ReactNode;
};
export const CommentActions = memo(
  ({
    onCancel,
    onEmojiPick,
    onLinkInsert,
    onLinkCancel,
    hasCancelButton,
    isDisabled,
    isLoadingMentions,
    addTriggerProgrammatically,
    shouldShowMentionsButton = true,
    hasInternalAvatar,
    getSelectedText,
    insertLinkButtonRef,
    helperActions,
  }: CommentActionsProps) => {
    const { isAboveMediumScreen } = useBreakpointsContext();

    return (
      <div
        className={classNames(
          'mr-0 ml-1 flex max-h-[40px] flex-1 items-center justify-between',
          hasInternalAvatar ? 'mt-1' : 'mt-0',
        )}
        data-testid={DISCUSSION_FORM_BUTTONS_CONTAINER}
      >
        {/* Left side - compose helper actions */}
        <div className="flex items-center gap-2">
          {helperActions ?? (
            <>
              {isAboveMediumScreen && (
                <EmojiMenu
                  trigger={
                    <IconButton
                      adsIcon
                      appearance="ghost"
                      color="gray"
                      label="Insert emoji"
                      icon={SmileIcon}
                      size="small"
                    />
                  }
                  // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
                  onSelect={(emoji) => onEmojiPick(emoji.native)}
                />
              )}

              {shouldShowMentionsButton && (
                <Tooltip label={isLoadingMentions ? 'Loading members…' : 'Mention someone'} side="top">
                  <IconButton
                    adsIcon
                    appearance="ghost"
                    color="gray"
                    data-testid="MENTION_TRIGGER_BUTTON"
                    disabled={isLoadingMentions}
                    label="Mention"
                    size="small"
                    onClick={addTriggerProgrammatically}
                    icon={AtSignIcon}
                  />
                </Tooltip>
              )}

              <InsertLinkButton
                onInsert={onLinkInsert}
                onCancel={onLinkCancel}
                getSelectedText={getSelectedText}
                ref={insertLinkButtonRef}
              />
            </>
          )}
        </div>

        {/* Right side - Cancel and Post buttons */}
        <div className="flex items-center gap-2">
          {hasCancelButton && (
            <Button
              appearance="ghost"
              color="gray"
              data-testid={DISCUSSION_FORM_BUTTON_CANCEL}
              onClick={onCancel}
              size="medium"
            >
              Cancel
            </Button>
          )}

          <Button
            appearance="filled"
            color="blue"
            data-testid={DISCUSSION_FORM_BUTTON_SUBMIT}
            disabled={isDisabled}
            size="medium"
            type="submit"
          >
            Post
          </Button>
        </div>
      </div>
    );
  },
);

CommentActions.displayName = 'CommentActions';
